import { useEffect, useState } from "react";

import { api } from "../api/rest";
import type { AccountStatus, ServiceStatus } from "../api/types";
import { useRefreshStatus, useStatus } from "../hooks/useStatus";
import { accountEngaged, accountReleasable, engagedAccounts } from "../lib/killswitch";

/**
 * Аварийный стоп.
 *
 * Асимметрия подтверждения — главное решение этого файла: ВКЛЮЧИТЬ стоп можно
 * одним кликом, СНЯТЬ — только в два шага. Опасное действие здесь не остановка,
 * а возобновление торговли, и подтверждать надо именно его.
 *
 * window.confirm не используется вовсе: он блокирует цикл событий, на котором
 * живёт весь сокетный интерфейс, и крадёт фокус ровно в ту минуту, когда рынок
 * нужен оператору больше всего.
 *
 * Правоприменение здесь отсутствует: кнопка пишет ключ risk:kill_switch, а
 * отказывает в отправке трейдер — он один читает ключ перед каждым ордером.
 */
export function KillSwitch() {
  const status = useStatus();
  const refresh = useRefreshStatus();

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = describe(status.data);

  // Подтверждение снятия само себя отменяет: незамеченная кнопка «точно снять»,
  // висящая на экране полчаса, — приглашение нажать её не глядя.
  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), 5000);
    return () => clearTimeout(timer);
  }, [confirming]);

  async function apply(on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.setKillSwitch(on);
      refresh();
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[var(--color-muted)]" title={state.hint}>
        {state.label}
      </span>

      {state.canEngage && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void apply(true)}
          className="rounded border border-[var(--color-sell)] px-2.5 py-1 text-xs font-medium text-[var(--color-sell)] hover:bg-[var(--color-sell)] hover:text-black disabled:opacity-50"
        >
          СТОП
        </button>
      )}

      {state.canRelease &&
        (confirming ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void apply(false)}
            className="rounded border border-amber-500 bg-amber-500/20 px-2.5 py-1 text-xs font-medium text-amber-300 disabled:opacity-50"
          >
            точно снять?
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(true)}
            className="rounded border border-[var(--color-border)] px-2.5 py-1 text-xs text-[var(--color-muted)] hover:text-white disabled:opacity-50"
          >
            снять стоп
          </button>
        ))}

      {error && <span className="text-xs text-[var(--color-sell)]">{error}</span>}
    </div>
  );
}

/**
 * Стопы аккаунтов в шапке (T5.6): по кнопке на слот рядом с общим стопом.
 *
 * Та же асимметрия, что у общего: взвести — одним кликом, снять — в два шага с
 * самоотменой через 5 с. Опасно возобновление, а не остановка.
 *
 * Компактно, одним словом слота: аккаунтов единицы, а шапка общая для всех
 * страниц. Состояние — словами трейдера (/status), а не ключа: торгует то, что
 * применил он.
 */
export function AccountKillSwitches() {
  const status = useStatus();
  const accounts = status.data?.accounts ?? [];
  if (accounts.length === 0) return null;
  return (
    <div className="flex items-center gap-1">
      {accounts.map((a) => (
        <AccountKillButton key={a.funder} account={a} />
      ))}
    </div>
  );
}

function AccountKillButton({ account }: { account: AccountStatus }) {
  const refresh = useRefreshStatus();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const engaged = accountEngaged(account);
  const releasable = accountReleasable(account);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), 5000);
    return () => clearTimeout(timer);
  }, [confirming]);

  async function apply(on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.setKillSwitch(on, account.funder);
      refresh();
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function click() {
    if (!engaged) return void apply(true);
    if (!releasable) return;
    if (!confirming) return setConfirming(true);
    void apply(false);
  }

  const title = error
    ? `${account.slot}: ${error}`
    : engaged
      ? releasable
        ? `${account.slot} остановлен из интерфейса. Нажать дважды — снять стоп аккаунта`
        : `${account.slot} остановлен не ключом интерфейса — кнопкой не снимается`
      : `${account.slot} торгует. Нажать — остановить только этот аккаунт`;

  return (
    <button
      type="button"
      disabled={busy || (engaged && !releasable)}
      onClick={click}
      title={title}
      className={`rounded border px-1.5 py-0.5 text-xs disabled:opacity-60 ${
        confirming
          ? "border-amber-500 bg-amber-500/20 text-amber-300"
          : engaged
            ? "border-[var(--color-sell)] bg-[var(--color-sell)]/15 text-[var(--color-sell)]"
            : error
              ? "border-[var(--color-sell)] text-[var(--color-muted)]"
              : "border-[var(--color-border)] text-[var(--color-muted)] hover:text-[var(--color-sell)]"
      }`}
    >
      {confirming ? `снять ${account.slot}?` : engaged ? `⛔ ${account.slot}` : account.slot}
    </button>
  );
}

/**
 * Полоса «торговля остановлена» и метка во вкладке.
 *
 * Заголовок вкладки меняется намеренно: оператор часто уходит на другую
 * вкладку, и стоп, видимый только на нашей странице, он заметит последним.
 * Полноэкранной заливки при этом нет — она конкурировала бы с цветами покупки и
 * продажи, на которых держится чтение стакана.
 */
export function KillSwitchBanner() {
  const status = useStatus();
  const engaged = isEngaged(status.data);

  useEffect(() => {
    const base = "pm_scalper";
    document.title = engaged ? `⛔ ТОРГОВЛЯ ОСТАНОВЛЕНА — ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [engaged]);

  // Остановленные аккаунты называются всегда: и под общим стопом (снимут
  // его — эти так и останутся стоять), и без него (остальные торгуют).
  const stopped = engagedAccounts(status.data);
  if (!engaged) {
    if (stopped.length === 0) return null;
    return (
      <div className="border-b border-amber-500/60 bg-amber-500/10 px-6 py-1.5 text-center text-xs font-medium text-amber-300">
        остановлены аккаунты: {stopped.join(", ")} — остальные торгуют
      </div>
    );
  }

  const state = describe(status.data);
  return (
    <div className="border-b border-[var(--color-sell)] bg-[var(--color-sell)]/15 px-6 py-2 text-center text-sm font-medium text-[var(--color-sell)]">
      ТОРГОВЛЯ ОСТАНОВЛЕНА — {state.hint}
      {stopped.length > 0 && ` Отдельно остановлены аккаунты: ${stopped.join(", ")}.`}
    </div>
  );
}

/** Стоп взведён, если так считает трейдер; при его молчании — если так говорит наш ключ. */
export function isEngaged(status: ServiceStatus | undefined): boolean {
  if (!status) return false;
  if (status.kill_switch !== null) return status.kill_switch;
  return status.kill_switch_ui === true;
}

interface KillState {
  label: string;
  hint: string;
  canEngage: boolean;
  canRelease: boolean;
}

/**
 * Четыре состояния стопа и что оператор может с ними сделать.
 *
 * Разведены они ради одного: стоп из окружения кнопкой НЕ снимается, и без
 * явного объяснения оператор жал бы «снять» и не понимал, почему ничего не
 * происходит.
 */
function describe(status: ServiceStatus | undefined): KillState {
  if (!status) {
    return { label: "стоп: неизвестно", hint: "состояние ещё не загружено", canEngage: false, canRelease: false };
  }

  const ui = status.kill_switch_ui === true;
  const env = status.kill_switch_env;

  if (!status.trader_reachable) {
    return {
      label: ui ? "стоп: включён" : "стоп: трейдер молчит",
      hint:
        "Трейдер не отвечает, фактическое состояние неизвестно. Из интерфейса стоп " +
        (ui ? "включён." : "снят.") +
        " Ключ пишется в Redis независимо от трейдера, поэтому кнопка работает.",
      canEngage: !ui,
      canRelease: ui,
    };
  }

  if (env === true && ui) {
    return {
      label: "стоп: включён (окружение + интерфейс)",
      hint:
        "Остановлено. Снятие из интерфейса торговлю НЕ вернёт: у трейдера " +
        "RISK_KILL_SWITCH=true. Правьте .env и перезапускайте трейдер.",
      canEngage: false,
      canRelease: true,
    };
  }
  if (env === true) {
    return {
      label: "стоп: включён (окружение)",
      hint:
        "Остановлено переменной RISK_KILL_SWITCH. Кнопка интерфейса здесь бессильна — " +
        "правьте .env и перезапускайте трейдер.",
      canEngage: false,
      canRelease: false,
    };
  }
  if (ui) {
    return {
      label: "стоп: включён (интерфейс)",
      hint: "Остановлено из интерфейса. Снятие вернёт торговлю сразу.",
      canEngage: false,
      canRelease: true,
    };
  }
  return {
    label: "торговля разрешена",
    hint: "Аварийный стоп снят. Ордера уходят на биржу.",
    canEngage: true,
    canRelease: false,
  };
}
